<script setup lang="ts">
import { computed } from 'vue';
import { injectPageLoadTracker } from '@integro/core';
import { usercontrolMessages } from '../../messages';
import { ItpLoadingIndicator, ItpLoadError, ItpReloadError } from '../loading';
import { ItpSkeleton } from '../skeleton';

type LoadingPlaceholder = 'spinner' | 'skeleton' | 'none';

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    reloading?: boolean;
    loadingSpinner?: boolean;
    loaded?: boolean;
    renderOnlyWhenLoadCompleted?: boolean;
    error?: Error | string | null;
    loadingPlaceholder?: LoadingPlaceholder;
    blocksPageLoad?: boolean;
    skeletonLines?: number;
  }>(),
  {
    loading: false,
    reloading: false,
    loadingSpinner: false,
    loaded: false,
    renderOnlyWhenLoadCompleted: false,
    error: null,
    loadingPlaceholder: 'spinner',
    blocksPageLoad: true,
    skeletonLines: 3,
  },
);

const emit = defineEmits<{
  (e: 'reload'): void;
}>();

const loadTracker = injectPageLoadTracker();

const pageOwnsTheIndicator = computed(
  () => !!loadTracker && loadTracker.state.value === 'loading',
);

const showSkeleton = computed(() => {
  if (props.loadingPlaceholder === 'none') {
    return false;
  }
  return (
    props.loadingPlaceholder === 'skeleton' ||
    (!props.blocksPageLoad && props.loadingPlaceholder === 'spinner')
  );
});

const showSpinner = computed(
  () =>
    props.loadingPlaceholder === 'spinner' &&
    !showSkeleton.value &&
    !pageOwnsTheIndicator.value,
);
</script>

<template>
  <div
    class="position-relative"
    :class="{
      'd-flex flex-column h-100':
        !loaded && (loadingPlaceholder !== 'none' || !!error),
    }"
  >
    <ItpLoadError
      v-if="error && !loaded"
      :error="error"
      :reloading="reloading"
      @reload="emit('reload')"
    />

    <slot v-else-if="loaded || loadingPlaceholder === 'none'" />

    <ItpSkeleton v-else-if="showSkeleton" :lines="skeletonLines" fill />

    <ItpLoadingIndicator
      v-else-if="showSpinner"
      fill
      :text="usercontrolMessages.loading"
    />

    <ItpReloadError
      v-if="loaded && error"
      :error="error"
      :reloading="reloading"
    />

    <ItpProgressLinear
      v-if="loadingSpinner && loading"
      class="w-100 position-absolute"
      style="top: 0"
    />
  </div>
</template>
